// ListPage — list-page composer (CHG-1225).
//
// Consumes a ListPageConfig from @generated/screens/<id>. Renders:
//   PageHeader (driven from config.title + config.actions) →
//   BulkActionBar (when bulkActions + selectedIds.length > 0) →
//   DataTable (driven from config.columns, fetching via config.queryFn)
//
// Per .forge/plans/v2-canary-pages.md §1.1 + dashboard-v2-stage-treatments.md.
// Consumer pages should be ~30-40 LOC: just <ListPage config={...} />.
//
// CHG-1330 / ITER-3457 — opt-in `bulkActions` slot (closes DRIFT-648).
// When the config declares bulk actions, ListPage takes ownership of the
// selectedIds state and wires it through to the DataTable + BulkActionBar.
// Selection is reset on page change / sort change / search change so a
// user can't accidentally bulk-act on rows they're no longer viewing.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { PageHeader, type PageHeaderAction } from "./PageHeader";
import { DataTable, type DataTableColumn } from "./DataTable";
import { BulkActionBar, type BulkAction } from "./BulkActionBar";
// FilterBar/FilterChip import retained for downstream future use (applied-state
// badges + Clear all affordance). The ListPage filter-chip row below uses a
// hand-rolled toggle button row because FilterBar's chip prop renders
// applied-state only ("label · value" with X to remove), not toggleable
// off/on. Keeping the import side-by-side documents the slot relationship.
// import { FilterBar, type FilterChip } from "./FilterBar";
import type { Entity } from "../../lib/stage-labels";
import type { ReactNode } from "react";
import { FILTER_OPTION_SOURCES, type FilterOption } from "./filterOptionSources";

// CHG-1666 / ITER-3778 — declarative filter-chip descriptor for the
// ListPage filterChips slot. A toggled chip's value flows into
// queryFn({ filters }) so the page-level fetch picks it up. Chips in
// the same `group` are mutually exclusive (selecting one replaces the
// previous); group-less chips toggle independently.
export interface FilterChipDescriptor {
  id: string;
  label: string;
  /** Value sent to queryFn under filters[field] when this chip is active. */
  field: string;
  value: string | number | boolean;
  /** Mutually-exclusive group id. At most one chip per group is active. */
  group?: string;
}

/**
 * CHG-1330 — descriptor declared in ListPageConfig. The spec-side fields
 * (id, label, permission) come from the entity-hint; `onAction` is the
 * hand-rolled handler the generator wraps in an override-block per
 * action so consumers can mount UI dropdowns / modals without touching
 * the generated config skeleton.
 */
export interface BulkActionDescriptor {
  id: string;
  label: string;
  icon?: ReactNode;
  variant?: "primary" | "secondary" | "danger";
  /** Required permission code (resolves to BulkActionBar permissionGate). */
  permission?: string;
  /** Behavior when the user lacks the permission. Defaults to "disable". */
  permissionFallback?: "hide" | "disable";
  onAction: (selectedIds: Array<string | number>) => void | Promise<void>;
}

/** CHG-1668 / ITER-3777 — declarative filter chip primitive for
 *  ListPageConfig. Renders as a labeled <select> in the filter row;
 *  selection updates a per-field filter value that flows into
 *  queryFn via `filters` and into queryKey for cache identity. Only
 *  type "select" is implemented today; "date-range" + "entity-search"
 *  are forward-promised (the generator emits them but ListPage
 *  renders a disabled placeholder chip until the relevant primitives
 *  land — DRIFT-714). */
export interface ListPageFilter {
  field: string;
  label: string;
  type: "select" | "date-range" | "entity-search";
  options?: Array<{ value: string; label: string }>;
  /** Optional target_entity for entity-search filters (forward-promised). */
  targetEntity?: string;
  /** CPE-FE2 — for type=select filters whose option set is a relationship
   * (not a static enum), the generator emits a `dynamicOptionsSource` key
   * instead of static `options`. ListPage resolves it against
   * FILTER_OPTION_SOURCES and renders the fetched options in the <select>.
   * This makes relationship filters (e.g. designation) genuinely functional
   * in v2 — the v2-native analogue of the v1 designation-filter dropdown. */
  dynamicOptionsSource?: string;
}

export interface ListPageConfig<T extends { id: string | number }> {
  /** Display name; goes into PageHeader.title. */
  title: string;
  subtitle?: string;
  /** react-query key + paged fetch fn. Receives {page, pageSize, sort, search, filters}. */
  queryKey: readonly unknown[];
  queryFn: (params: {
    page: number;
    pageSize: number;
    sortField?: string;
    sortDirection?: "asc" | "desc";
    search?: string;
    // CHG-1668 / ITER-3777 + CHG-1666 / ITER-3778 + CHG-1676 / ITER-3785 —
    // merged filter dict shared by both the select-dropdown row
    // (config.filters ListPageFilter[]) and the toggle-chip row
    // (config.filterChips FilterChipDescriptor[]). Both signals merge
    // into this one map before being forwarded to queryFn. Wider value
    // type than the CHG-1668 original to accommodate boolean + number
    // chip values (e.g., has_recurring=true, days_since_last_gift=30)
    // and `undefined` from dropdown "All" selections.
    filters?: Record<string, string | number | boolean | undefined>;
  }) => Promise<{ items: T[]; total: number }>;
  columns: DataTableColumn<T>[];
  /** Row click → navigate to this template. {id} substitution from row.id. */
  rowHref?: string;
  /** Default sort (primary clause). */
  defaultSort?: { field: string; direction: "asc" | "desc" };
  /** CHG-1359 — full list of default sort clauses in declared order. Emitted
   * by `v2gen.py` only when the entity-hint declares 2+ sort clauses.
   * Consumed by no runtime yet (the active cascade in `ListPage` still
   * reads `defaultSort` only); reserved for a future multi-sort comparator.
   * `defaultSort` above MUST mirror the first entry when this field is set. */
  defaultSorts?: Array<{ field: string; direction: "asc" | "desc"; nulls?: "first" | "last" }>;
  /** Header action buttons. */
  headerActions?: PageHeaderAction[];
  /** Stage-ambient-row treatment (optional). */
  ambientRow?: { stageField: string; entity: Entity; activeStages: string[] };
  /** Search box config. */
  search?: { enabled: boolean; placeholder?: string };
  pageSize?: number;
  emptyState?: { title: string; subtitle?: string };
  /** CHG-1330 — opt-in bulk-action descriptors. Renders BulkActionBar above
   * the table when at least one row is selected. Enables DataTable
   * selectable mode automatically. */
  bulkActions?: BulkActionDescriptor[];
  /** CHG-1666 / ITER-3778 — opt-in filter-chip row above the table. Each
   * descriptor declares a (field, value) tuple sent through queryFn when
   * the chip is toggled on. Chips sharing a `group` are mutually
   * exclusive. Coexists with `filters` below; both signals merge into
   * the same queryFn.filters dict at fetch time. */
  filterChips?: FilterChipDescriptor[];
  /** CHG-1668 / ITER-3777 + CHG-1676 / ITER-3785 — opt-in select-dropdown
   * filter row rendered above the table. Order = display order.
   * ListPage owns the selection state; queryFn receives the current
   * selection via `filters` and queryKey is invalidated on any change.
   * Re-introduced in CHG-1676 after the CHG-1665/1666/1668 merge
   * collision dropped the type while leaving the render block intact. */
  filters?: ListPageFilter[];
}

export interface ListPageProps<T extends { id: string | number }> {
  config: ListPageConfig<T>;
}

export function ListPage<T extends { id: string | number }>({ config }: ListPageProps<T>) {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState(config.defaultSort?.field);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | undefined>(config.defaultSort?.direction);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([]);
  // CHG-1666 / ITER-3778 — active filter-chip ids (subset of
  // config.filterChips[].id). Maintained as a stable array; resolved to
  // {field → value} pairs and merged with filterValues at fetch time.
  const [activeChipIds, setActiveChipIds] = useState<string[]>([]);
  // CHG-1668 / ITER-3777 + CHG-1676 / ITER-3785 — select-dropdown filter
  // values keyed by ListPageFilter.field. Updated via per-chip onChange
  // in the render row below. All filter changes reset to page 0 so the
  // operator doesn't end up on an empty page-3 after narrowing the set.
  const [filterValues, setFilterValues] = useState<Record<string, string | undefined>>({});
  const pageSize = config.pageSize ?? 50;
  const hasBulkActions = !!(config.bulkActions && config.bulkActions.length > 0);
  const filterChips = config.filterChips ?? [];
  const hasFilterChips = filterChips.length > 0;

  // Clear selection when the visible row set changes — bulk-acting on
  // off-screen rows is surprising. Tested in ListPage.test.tsx.
  useEffect(() => {
    if (hasBulkActions && selectedIds.length > 0) setSelectedIds([]);
    // Intentional: depend on view-defining params, not selectedIds (avoids
    // infinite reset loop when user is actively selecting).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortField, sortDirection, search, filterValues, activeChipIds.join("|")]);

  // CHG-1666 / ITER-3778 + CHG-1668 / ITER-3777 + CHG-1676 / ITER-3785 —
  // resolve both signal sources into a single dict for queryFn. Chip
  // values (typed string|number|boolean) win over dropdown values
  // (typed string|undefined) when the same field is keyed by both —
  // chip semantics are stricter (mutually-exclusive group + boolean
  // toggle), so they take precedence. Undefined values from the
  // dropdown row are dropped before merging so they don't shadow
  // active chip values.
  const filters = useMemo<Record<string, string | number | boolean | undefined>>(() => {
    const merged: Record<string, string | number | boolean | undefined> = {};
    for (const [k, v] of Object.entries(filterValues)) {
      if (v !== undefined) merged[k] = v;
    }
    if (hasFilterChips && activeChipIds.length > 0) {
      for (const id of activeChipIds) {
        const desc = filterChips.find((c) => c.id === id);
        if (desc) merged[desc.field] = desc.value;
      }
    }
    return merged;
  }, [filterValues, activeChipIds, filterChips, hasFilterChips]);

  // CPE-FE2 — resolve dynamic select-filter options (e.g. designation) once
  // per source key. The distinct source keys come from config.filters; the
  // result map (source → options[]) is consumed in the filter render row.
  const dynamicSourceKeys = useMemo(
    () =>
      Array.from(
        new Set(
          (config.filters ?? [])
            .map((f) => f.dynamicOptionsSource)
            .filter((k): k is string => !!k),
        ),
      ).sort(),
    [config.filters],
  );
  const dynamicOptionsQuery = useQuery({
    queryKey: ["list-page", "filter-options", dynamicSourceKeys],
    enabled: dynamicSourceKeys.length > 0,
    queryFn: async () => {
      const out: Record<string, FilterOption[]> = {};
      await Promise.all(
        dynamicSourceKeys.map(async (key) => {
          const loader = FILTER_OPTION_SOURCES[key];
          out[key] = loader ? await loader() : [];
        }),
      );
      return out;
    },
  });
  const dynamicOptions = dynamicOptionsQuery.data ?? {};

  const query = useQuery({
    queryKey: [...config.queryKey, { page, pageSize, sortField, sortDirection, search, filters }],
    queryFn: () => config.queryFn({
      page, pageSize, sortField, sortDirection,
      search: search || undefined,
      filters: Object.keys(filters).length > 0 ? filters : undefined,
    }),
    placeholderData: keepPreviousData,
  });

  const data = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const bulkActions: BulkAction[] = (config.bulkActions ?? []).map((d) => ({
    id: d.id,
    label: d.label,
    icon: d.icon,
    variant: d.variant,
    onClick: () => { void d.onAction(selectedIds); },
    permissionGate: d.permission
      ? { require: d.permission, fallback: d.permissionFallback ?? "disable" }
      : undefined,
  }));

  function toggleChip(chipId: string) {
    const desc = filterChips.find((c) => c.id === chipId);
    if (!desc) return;
    setActiveChipIds((prev) => {
      const alreadyActive = prev.includes(chipId);
      if (alreadyActive) return prev.filter((x) => x !== chipId);
      if (desc.group) {
        const sameGroup = filterChips.filter((c) => c.group === desc.group).map((c) => c.id);
        return [...prev.filter((x) => !sameGroup.includes(x)), chipId];
      }
      return [...prev, chipId];
    });
    setPage(0);
  }

  // Render an inline button row for filterChips (toggle behavior). Active
  // chips render with a tinted background; inactive chips render with the
  // subtle bg. Click toggles. Distinct from FilterBar's chip prop which
  // is for active-only "X to remove" badges — that's used for displaying
  // applied state only, not for toggling. We render BOTH here: the button
  // row is the source of truth; FilterBar's chips render the applied set
  // for parity with sibling pages and for "Clear all" affordance.
  return (
    <main style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px 48px" }}>
      <PageHeader
        title={config.title}
        subtitle={config.subtitle}
        actions={config.headerActions}
      />
      {hasFilterChips ? (
        <div data-testid="filter-chip-row" style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "8px 0" }}>
          {filterChips.map((c) => {
            const active = activeChipIds.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                data-chip-id={c.id}
                data-chip-active={active ? "true" : "false"}
                onClick={() => toggleChip(c.id)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 30,
                  padding: "0 12px",
                  fontSize: 12,
                  fontWeight: 500,
                  color: active ? "var(--color-primary-contrast, white)" : "var(--color-text-secondary)",
                  background: active ? "var(--color-primary)" : "var(--color-bg-subtle)",
                  border: active ? "1px solid var(--color-primary)" : "1px solid var(--color-border-subtle)",
                  borderRadius: 9999,
                  cursor: "pointer",
                }}
              >
                {c.label}
              </button>
            );
          })}
          {activeChipIds.length > 0 ? (
            <button
              type="button"
              onClick={() => { setActiveChipIds([]); setPage(0); }}
              style={{
                marginLeft: "auto",
                background: "transparent",
                border: "none",
                color: "var(--color-text-muted)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                padding: "4px 8px",
              }}
            >
              Clear filters
            </button>
          ) : null}
        </div>
      ) : null}
      {hasBulkActions ? (
        <div style={{ marginBottom: 12 }}>
          <BulkActionBar
            selectedCount={selectedIds.length}
            actions={bulkActions}
            onClear={() => setSelectedIds([])}
          />
        </div>
      ) : null}
      {config.filters && config.filters.length > 0 ? (
        <div
          data-testid="list-page-filters"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            padding: "4px 0",
          }}
        >
          {config.filters.map((f) => {
            // CHG-1668 / ITER-3777 — forward-promised filter types
            // render as a disabled chip until ListPage gains the
            // relevant primitive. The generator + entity-hint can
            // declare these today so the spec stays accurate.
            if (f.type !== "select") {
              return (
                <span
                  key={f.field}
                  data-testid={`filter-chip-${f.field}`}
                  data-filter-deferred="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 10px",
                    fontSize: 12,
                    color: "var(--color-text-muted)",
                    background: "var(--color-surface)",
                    border: "1px dashed var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    opacity: 0.6,
                    cursor: "not-allowed",
                  }}
                  title={`${f.label} filter — coming soon (DRIFT-714)`}
                >
                  {f.label}: <span style={{ textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>SOON</span>
                </span>
              );
            }
            const current = filterValues[f.field] ?? "";
            return (
              <label
                key={f.field}
                data-testid={`filter-chip-${f.field}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 8px 4px 10px",
                  fontSize: 12,
                  color: "var(--color-text)",
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <span style={{ color: "var(--color-text-muted)" }}>{f.label}:</span>
                <select
                  value={current}
                  onChange={(e) => {
                    const v = e.target.value || undefined;
                    setFilterValues((prev) => ({ ...prev, [f.field]: v }));
                    setPage(0);
                  }}
                  style={{
                    appearance: "none",
                    border: "none",
                    background: "transparent",
                    fontSize: 12,
                    color: "var(--color-text)",
                    fontFamily: "inherit",
                    cursor: "pointer",
                  }}
                >
                  <option value="">All</option>
                  {(f.dynamicOptionsSource
                    ? dynamicOptions[f.dynamicOptionsSource] ?? []
                    : f.options ?? []
                  ).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
          {Object.values(filterValues).some((v) => v) ? (
            <button
              type="button"
              onClick={() => { setFilterValues({}); setPage(0); }}
              data-testid="filter-clear-all"
              style={{
                fontSize: 11,
                color: "var(--color-text-muted)",
                background: "transparent",
                border: "none",
                padding: "4px 6px",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Clear filters
            </button>
          ) : null}
        </div>
      ) : null}
      <DataTable
        data={data}
        columns={config.columns}
        isLoading={query.isLoading}
        error={query.isError ? (query.error as Error) : null}
        onRetry={() => query.refetch()}
        ambientRow={config.ambientRow}
        pagination={{ page, pageSize, total }}
        onPageChange={setPage}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={(field, direction) => { setSortField(field); setSortDirection(direction); setPage(0); }}
        searchValue={config.search?.enabled ? search : undefined}
        searchPlaceholder={config.search?.placeholder}
        onSearchChange={config.search?.enabled ? (v) => { setSearch(v); setPage(0); } : undefined}
        onRowClick={config.rowHref ? (row) => navigate(config.rowHref!.replace("{id}", String(row.id))) : undefined}
        emptyState={config.emptyState}
        selectable={hasBulkActions}
        selectedIds={hasBulkActions ? selectedIds : undefined}
        onSelectionChange={hasBulkActions ? setSelectedIds : undefined}
      />
    </main>
  );
}
